/**
 * HexDB Sentinel — Variable Engine
 * Metabase-style {{variable}} support for SQL templates.
 *
 * Syntax:  {{name}}  {{name:type}}  {{name:type=default}}
 * Types:   text, number, date, dropdown
 */

const VAR_PATTERN = /\{\{([a-zA-Z_][a-zA-Z0-9_]*)(?::([a-z]+))?(?:=([^}]*))?\}\}/g;

export function extractVariables(sqlTemplate) {
  if (!sqlTemplate || typeof sqlTemplate !== 'string') return [];
  const found = {};
  let match;
  VAR_PATTERN.lastIndex = 0;
  while ((match = VAR_PATTERN.exec(sqlTemplate)) !== null) {
    const name = match[1];
    const type = match[2] || null;
    const defaultValue = match[3] !== undefined ? match[3] : null;
    if (!found[name]) {
      found[name] = { name, type: type || 'text', defaultValue, positions: [] };
    } else {
      if (type && !found[name].type) found[name].type = type;
      if (defaultValue !== null && found[name].defaultValue === null) found[name].defaultValue = defaultValue;
    }
    found[name].positions.push({ start: match.index, end: match.index + match[0].length });
  }
  return Object.values(found);
}

export function hasVariables(sql) {
  if (!sql) return false;
  VAR_PATTERN.lastIndex = 0;
  return VAR_PATTERN.test(sql);
}

function formatLabel(name) {
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function mergeWithDefinitions(extracted, definitions = []) {
  const defMap = {};
  for (const def of definitions) defMap[def.name] = def;
  return extracted.map(v => {
    const def = defMap[v.name];
    if (def) {
      return {
        name: v.name, type: def.type || v.type || 'text', label: def.label || formatLabel(v.name),
        defaultValue: def.defaultValue ?? v.defaultValue, options: def.options || null,
        required: def.required !== false, placeholder: def.placeholder || null,
        min: def.min ?? null, max: def.max ?? null, positions: v.positions,
      };
    }
    return {
      name: v.name, type: v.type || 'text', label: formatLabel(v.name),
      defaultValue: v.defaultValue, options: null, required: true,
      placeholder: null, min: null, max: null, positions: v.positions,
    };
  });
}

export function validateVariables(specs, values) {
  const errors = {};
  for (const spec of specs) {
    const val = values[spec.name];
    const isEmpty = val === undefined || val === null || val === '';
    if (spec.required && isEmpty && spec.defaultValue === null) {
      errors[spec.name] = `${spec.label || spec.name} is required`;
      continue;
    }
    if (isEmpty) continue;
    switch (spec.type) {
      case 'number': {
        const num = Number(val);
        if (isNaN(num)) errors[spec.name] = `${spec.label} must be a number`;
        else if (spec.min !== null && num < spec.min) errors[spec.name] = `Min ${spec.min}`;
        else if (spec.max !== null && num > spec.max) errors[spec.name] = `Max ${spec.max}`;
        break;
      }
      case 'date':
        if (!/^\d{4}-\d{2}-\d{2}$/.test(val)) errors[spec.name] = `${spec.label} must be YYYY-MM-DD`;
        break;
      case 'dropdown':
        if (spec.options?.length > 0) {
          const vals = spec.options.map(o => typeof o === 'object' ? o.value : o);
          if (!vals.includes(val)) errors[spec.name] = `${spec.label} must be a valid option`;
        }
        break;
    }
  }
  return Object.keys(errors).length === 0 ? { valid: true, errors: {} } : { valid: false, errors };
}

function getSafeValue(value, type) {
  if (type === 'number') { const n = Number(value); return isNaN(n) ? '0' : String(n); }
  if (type === 'dropdown') { const n = Number(value); if (!isNaN(n) && value.trim() !== '') return String(n); }
  return `'${String(value).replace(/'/g, "''")}'`;
}

function escapeRegex(str) { return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

export function substituteVariables(sqlTemplate, specs, values) {
  const substitutions = {};
  let result = sqlTemplate;
  for (const spec of specs) {
    const val = values[spec.name] !== undefined && values[spec.name] !== '' ? values[spec.name] : spec.defaultValue;
    if (val === null || val === undefined) continue;
    const safeVal = getSafeValue(val, spec.type);
    substitutions[spec.name] = { raw: val, safe: safeVal, type: spec.type };
    const pat = new RegExp('\\{\\{' + escapeRegex(spec.name) + '(?::[a-z]+)?(?:=[^}]*)?\\}\\}', 'g');
    result = result.replace(pat, safeVal);
  }
  return { sql: result, substitutions };
}

export function buildDefinitions(extracted, overrides = {}) {
  return extracted.map(v => {
    const ov = overrides[v.name] || {};
    return {
      name: v.name, type: ov.type || v.type || 'text', label: ov.label || formatLabel(v.name),
      defaultValue: ov.defaultValue ?? v.defaultValue ?? null, options: ov.options || null,
      required: ov.required !== undefined ? ov.required : true, placeholder: ov.placeholder || null,
      min: ov.min ?? null, max: ov.max ?? null,
    };
  });
}

export function hasUnresolvedVariables(sql) { return hasVariables(sql); }